import { Button, Divider, message, Modal, Tooltip } from 'antd';
import FormField from 'components/FormField';
import { useTranslate } from '@tolgee/react';
import Icon from '@ant-design/icons';
import { ReactComponent as GoogleSvg } from 'assets/icons/google.svg';
import { ReactComponent as FacebookSvg } from 'assets/icons/facebook.svg';
import { useMutation } from '@tanstack/react-query';
import {
  externalLoginBind,
  externalLoginCheck,
  ExternalLoginCheckResponse,
  externalLoginUnbind,
} from 'services/api/admin';
import { find, get, lowerCase, upperCase } from 'lodash-es';
import { useFormikContext } from 'formik';
import useIsPasswordSet from 'hooks/business/user/useIsPasswordSet';
import { useEffect, useMemo, useState } from 'react';
import {
  generateFacebookAuthUrl,
  generateGoogleAuthUrl,
  getFacebookSdkReady,
  getGoogleSdkReady,
  transformAppId,
} from 'views/Startups/Login/Auth2';
import chatttSDK from 'services/chattt';
import { BindLogins } from 'services/api/assistant.account';
import { queryAssistant } from 'services/api/assistant.account';
import { useAuthorization } from '../../../../components/Authorization';
import { useTransAppProductName } from 'hooks/useTrans';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useLatest } from 'ahooks';
import { openExternalURL } from 'utils/openExternalURL';
import { useCountryCodeValue } from 'hooks/business/system/useCountryCode';

export const Auth2Detail = (props: { auth2Props: BindLogins }) => {
  const { auth2Props } = props;
  const { t } = useTranslate();
  const { initialValues } = useFormikContext<any>();
  const isPwdSet = useIsPasswordSet();
  const [bindLogins, setBindLogins] = useState<BindLogins>(auth2Props);
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const appProductNameT = useTransAppProductName();
  const lang = useSystemLanguageValue();
  const langRef = useLatest(lang);
  const cc = useCountryCodeValue();
  console.log('countryCode', chatttSDK?.app?.getLocaleCountryCode?.());
  const [ready, setReady] = useState(() => {
    return chatttSDK?.app?.getLocaleCountryCode?.()
      ? chatttSDK?.app?.getLocaleCountryCode?.()?.toUpperCase() !== 'CN'
      : cc
      ? cc !== 'CN'
      : false;
  });
  const [googleLoading, setGoogleLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  useEffect(() => {
    if (cc && !chatttSDK?.app?.getLocaleCountryCode?.()) {
      setReady((prevState) => {
        return prevState ? true : cc !== 'CN';
      });
    }
  }, [cc]);

  const { mutate: refresh } = useMutation({
    mutationKey: ['bindLogins'],
    mutationFn: () => {
      return queryAssistant({
        user_ids: [`${payload?.id}`],
      });
    },
    onSuccess: (res) => {
      setBindLogins(res[0].bindLogins);
    },
  });

  const hasOneLoginWay = useMemo(() => {
    return isPwdSet ? initialValues.email || initialValues.account : !!initialValues.phoneNumber;
  }, [isPwdSet, initialValues]);

  const handleCheckResult = (res: ExternalLoginCheckResponse, type: 'Google' | 'Facebook') => {
    if (res.type === 'SUCCESS') {
      externalLoginBind({ uuid: res.uuid })
        .then(() => {
          message.success(t('已绑定'));
          refresh();
        })
        .catch(() => {
          message.error(t('请稍后再试'));
        });
    } else if (res.type === 'ONLY_CAN_EXTERNAL_LOGIN') {
      Modal.confirm({
        title: t('aide.提示'),
        content: appProductNameT(
          '该[{x}]账号已绑定{appProductName}账号(y)，且旧账号仅可使用[{x}]账号快捷登录，换绑后将无法登录旧账号，确认要换绑到当前账号吗？',
          {
            x: type,
            y: res.account,
          },
        ),
        onOk: () => {
          return externalLoginBind({ uuid: res.uuid })
            .then(() => {
              message.success(t('已绑定'));
              refresh();
            })
            .catch(() => {
              message.error(t('请稍后再试'));
            });
        },
      });
    } else if (res.type === 'EXTERNAL_LOGIN_AND_OTHER_LOGIN') {
      Modal.confirm({
        title: t('aide.提示'),
        content: appProductNameT(
          '该[{x}]账号已绑定{appProductName}其他账号({y})，确认要换绑到当前账号吗？',
          {
            x: type,
            y: res.account,
          },
        ),
        onOk: () => {
          return externalLoginBind({ uuid: res.uuid })
            .then(() => {
              message.success(t('已绑定'));
              refresh();
            })
            .catch(() => {
              message.error(t('请稍后再试'));
            });
        },
      });
    }
  };

  const tryGoogleAuth = () => {
    setGoogleLoading(true);
    getGoogleSdkReady()
      .then(async () => {
        const appInfo = await chatttSDK?.app?.info?.();

        if (appInfo?.appId) {
          const state = {
            appId: transformAppId(appInfo.appId),
            moduleName: 'codeVerify',
            action: 'auth2',
            lang: langRef.current,
            way: 'google',
          };
          const googleAuthUrl = generateGoogleAuthUrl(state, true);
          openExternalURL(googleAuthUrl);
        } else {
          // 走弹窗逻辑
          const client = window.google.accounts.oauth2.initTokenClient({
            client_id: '711456311901-p2gm2hc3tml353c431esjes09snpg7qt.apps.googleusercontent.com',
            scope:
              'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
            callback: (res: any) => {
              const accessToken = res.access_token;
              setGoogleLoading(true);
              externalLoginCheck({
                type: 'GOOGLE',
                code: accessToken,
                redirectUrl: '',
                authType: 'accessToken',
              })
                .then((res) => {
                  handleCheckResult(res, 'Google');
                })
                .catch(() => {
                  message.error(t('请稍后再试'));
                })
                .finally(() => {
                  setGoogleLoading(false);
                });
            },
          });
          client?.requestAccessToken();
        }
      })
      .catch(() => {
        message.error(t('请稍后再试'));
      })
      .finally(() => {
        setGoogleLoading(false);
      });
  };

  const tryFacebookAuth = () => {
    setFacebookLoading(true);
    getFacebookSdkReady()
      .then(async () => {
        const appInfo = await chatttSDK?.app?.info?.();
        if (appInfo?.appId) {
          const state = {
            appId: transformAppId(appInfo.appId),
            moduleName: 'codeVerify',
            action: 'auth2',
            lang: langRef.current,
            way: 'facebook',
          };
          const facebookAuthUrl = generateFacebookAuthUrl(state, true);
          openExternalURL(facebookAuthUrl);
        } else {
          const cb = (response: any) => {
            setFacebookLoading(true);
            externalLoginCheck({
              type: 'FACEBOOK',
              code: response.authResponse.accessToken,
              redirectUrl: '',
              authType: 'accessToken',
            })
              .then((res) => {
                handleCheckResult(res, 'Facebook');
              })
              .catch(() => {
                message.error(t('请稍后再试'));
              })
              .finally(() => {
                setFacebookLoading(false);
              });
          };
          window.FB.getLoginStatus((response) => {
            if (response.status === 'connected') {
              cb(response);
            } else {
              window.FB.login(
                function (response) {
                  if (response.status === 'connected') {
                    console.log(response);
                    cb(response);
                  } else {
                    message.error(t('请稍后再试'));
                  }
                },
                {
                  scope: 'public_profile,email',
                },
              );
            }
          });
        }
      })
      .catch(() => {
        message.error(t('请稍后再试'));
      })
      .finally(() => {
        setFacebookLoading(false);
      });
  };

  const tryUnbind = (type: 'Google' | 'Facebook') => {
    // unbind
    Modal.confirm({
      title: t('确认要解绑吗？'),
      content: t('解绑后，将无法使用[Google/Facebook]账号快捷登录', { x: type }),
      onOk: () => {
        return externalLoginUnbind({ type: upperCase(type) as any })
          .then(() => {
            message.success(t('已解绑'));
            refresh();
          })
          .catch(() => {
            message.error(t('请稍后再试'));
          });
      },
    });
  };

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const { event: eventName, payload } = event.data;
      if (eventName === 'app:link') {
        const { moduleName, action, jsonParams } = payload;
        let jsonData;
        try {
          jsonData = JSON.parse(atob(decodeURIComponent(jsonParams)));
          console.log('jsonData', jsonData);
        } catch (err) {
          console.log('[jsonParams] parse error');
        }

        if (action === 'auth2' && moduleName === 'codeVerify') {
          const { way, code } = jsonData;
          if (way === 'facebook') {
            setFacebookLoading(true);
          } else {
            setGoogleLoading(true);
          }
          externalLoginCheck({
            type: lowerCase(way) as any,
            code,
            redirectUrl:
              way === 'facebook'
                ? 'https://applink.zhisuitech.com/'
                : 'https://applink.zhisuitech.com',
            authType: 'code',
          })
            .then((res) => {
              handleCheckResult(res, 'Facebook');
            })
            .catch(() => {
              message.error(t('请稍后再试'));
            })
            .finally(() => {
              if (way === 'facebook') {
                setFacebookLoading(false);
              } else {
                setGoogleLoading(false);
              }
            });
        }
      }
    };
    window.addEventListener('message', handler, false);
    return () => {
      window.removeEventListener('message', handler, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ready) {
    return null;
  }
  return (
    <div className="mt-[-4px]">
      <Divider className="!my-[8px]" />
      <FormField name="nothing" label={t('绑定第三方账户')}>
        {() => {
          const google = find(bindLogins, { type: 'GOOGLE' });
          const facebook = find(bindLogins, { type: 'FACEBOOK' });
          const googleUnbindDisabled =
            !(hasOneLoginWay || get(facebook, 'isBind', false)) && get(google, 'isBind', false);
          const facebookUnbindDisabled =
            !(hasOneLoginWay || get(google, 'isBind', false)) && get(facebook, 'isBind', false);
          return (
            <div className="flex gap-[16px]">
              <div className="bg-[#f7f9fa] h-[40px] flex items-center justify-between basis-1/2 flex-1 h-[40px] rounded-[7px] px-[8px] overflow-hidden">
                <div className="flex items-center gap-[4px]">
                  <Icon component={GoogleSvg} />
                  {google?.isBind ? get(google, 'name') : t('Google账号')}
                </div>

                <Tooltip
                  overlayClassName={!googleUnbindDisabled ? '!hidden' : ''}
                  title={t(
                    '此账号登录方式唯一，不可以解绑，设置账号密码或手机号等其他登录方式后可以解绑',
                  )}
                >
                  <Button
                    onClick={() => {
                      if (get(google, 'isBind', false)) {
                        tryUnbind('Google');
                      } else {
                        tryGoogleAuth();
                      }
                    }}
                    loading={googleLoading}
                    disabled={googleUnbindDisabled}
                    type="link"
                    className="!p-0"
                  >
                    {get(google, 'isBind', false) ? t('解绑') : t('绑定')}
                  </Button>
                </Tooltip>
              </div>

              <div className="bg-[#f7f9fa] h-[40px] flex items-center justify-between basis-1/2 flex-1 h-[40px] rounded-[7px] px-[8px] overflow-hidden">
                <div className="flex items-center gap-[4px]">
                  <Icon component={FacebookSvg} />
                  {facebook?.isBind ? get(facebook, 'name') : t(`Facebook账号`)}
                </div>

                <Tooltip
                  overlayClassName={!facebookUnbindDisabled ? '!hidden' : ''}
                  title={t('此账号登录方式唯一，不可以解绑，设置账号密码或手机号后可以解绑')}
                >
                  <Button
                    onClick={() => {
                      if (get(facebook, 'isBind', false)) {
                        tryUnbind('Facebook');
                      } else {
                        tryFacebookAuth();
                      }
                    }}
                    loading={facebookLoading}
                    disabled={facebookUnbindDisabled}
                    type="link"
                    className="!p-0"
                  >
                    {get(facebook, 'isBind', false) ? t('解绑') : t('绑定')}
                  </Button>
                </Tooltip>
              </div>
            </div>
          );
        }}
      </FormField>
    </div>
  );
};
